<script lang="ts">
  import {
    DateFormatter,
    toCalendarDate,
    type DateValue,
    type ZonedDateTime,
  } from "@internationalized/date";
  import { createDateRangePicker, melt } from "@melt-ui/svelte";
  import { fly } from "svelte/transition";
  import type { DateRangePickerProps, ZonedDateRange } from "./types.js";
  import {
    ChevronDown,
    Calendar,
    ChevronRight,
    ChevronLeft,
  } from "@poolsideai/web-components/icons";
  import {
    DATE_RANGE_PRESETS,
    endOfDay,
    getPresetRange,
    startOfDay,
    type DateRangePreset,
  } from "./utils.js";
  import { onDestroy, tick } from "svelte";

  // Preset clicks bypass the startOfDay/endOfDay normalization below so
  // rolling windows like "Last 24h" stay exact (now-24h → now). Calendar
  // picks get snapped.
  //
  // Safe because melt-ui's `overridable` store invokes `onValueChange`
  // synchronously within `selectedRange.set()` — we set the flag, call set,
  // the callback runs and clears the flag, all inline. If melt ever moves
  // that callback off the synchronous path this will silently stop
  // normalizing calendar picks; at that point, encode the signal in the
  // payload instead of module-scoped state.
  let skipNormalize = false;

  let {
    minValue,
    maxValue,
    userLocale = typeof navigator !== "undefined" ? navigator.language : "en-US",
    onChange,
    value,
    defaultValue,
    showPresets,
    label,
  }: DateRangePickerProps = $props();

  const {
    elements: { calendar, cell, content, grid, heading, nextButton, prevButton, trigger },
    states: { months, headingValue, open, value: selectedRange },
    helpers: { isDateDisabled, isDateUnavailable, isSelected },
  } = createDateRangePicker({
    onValueChange: ({ next }) => {
      if (skipNormalize) {
        skipNormalize = false;
        return next;
      }

      let { start, end } = next;
      if (start) {
        start = startOfDay(start as ZonedDateTime, minValue);
      }
      if (end) {
        end = endOfDay(end as ZonedDateTime, maxValue);
      }

      return { start, end };
    },
    // svelte-ignore state_referenced_locally
    ...(value ? { value } : {}),
    forceVisible: true,
    // svelte-ignore state_referenced_locally
    locale: userLocale,
    // svelte-ignore state_referenced_locally
    defaultValue: defaultValue ?? getPresetRange("last7d", maxValue),
    // svelte-ignore state_referenced_locally
    minValue,
    // svelte-ignore state_referenced_locally
    maxValue,
  });

  let displayedRange = $state<ZonedDateRange | null>(null);

  // melt-ui builds weekday headers via Intl.DateTimeFormat without a timeZone,
  // so a ZonedDateTime cell with a UTC-edge time (e.g. 23:59 UTC) gets
  // formatted in the runtime's local zone and rolls into the next calendar
  // day — producing headers misaligned by one column. Format using each cell's
  // own timeZone instead so UTC pickers render UTC weekdays regardless of
  // when the page loads or where the user is.
  const weekdayHeaders = $derived.by(() => {
    const firstWeek = $months?.[0]?.weeks?.[0];
    if (!firstWeek) return [];
    return firstWeek.map((date) => {
      const zoned = date as ZonedDateTime;
      return new Intl.DateTimeFormat(userLocale, {
        weekday: "narrow",
        timeZone: zoned.timeZone,
      }).format(zoned.toDate());
    });
  });

  function formatDateRange(range: ZonedDateRange | null) {
    if (!range) return "Select a date range";
    // Format in the range's own timezone so UTC-anchored pickers display UTC
    // dates, local-anchored pickers display local dates, etc. Without this,
    // Intl.DateTimeFormat defaults to the runtime's local tz and can mislabel
    // dates near UTC midnight for users with large negative offsets.

    const fmt = new DateFormatter(userLocale, {
      month: "short",
      day: "numeric",
      timeZone: range.start.timeZone,
    });

    const dates = fmt.formatRange(range.start.toDate(), range.end.toDate());

    const zone = new DateFormatter(userLocale, {
      timeZoneName: "short",
      timeZone: range.start.timeZone,
    });

    const tz = zone
      .formatToParts(range.start.toDate())
      .find((p) => p.type === "timeZoneName")?.value;

    return `${dates} ${tz}`;
  }

  // Presets bypass the start/end-of-day normalization so they remain exact
  // rolling windows; calendar picks get snapped in the effect below.

  function applyPreset(preset: DateRangePreset) {
    skipNormalize = true;
    selectedRange.set(getPresetRange(preset.key, maxValue));
  }

  function maybeSelectMaxDate(e: Event, date: DateValue) {
    // melt doesn't let us select maxValue as both start + end of range
    // so we have to allow it manually
    if (!$selectedRange.start && !$selectedRange.end && $isSelected(date)) {
      e.preventDefault();
      $selectedRange.start = date;
      $selectedRange.end = date;
    }
  }

  function handleMaxDateKeydown(e: KeyboardEvent, date: DateValue) {
    if (e.key === " " || e.key === "Enter") {
      maybeSelectMaxDate(e, date);
    }
  }

  $effect(() => {
    if (!$selectedRange) {
      return;
    }

    const start = $selectedRange.start as ZonedDateTime | undefined;
    const end = $selectedRange.end as ZonedDateTime | undefined;
    if (!start || !end) {
      return;
    }

    const range: ZonedDateRange = { start, end };
    displayedRange = range;
    onChange?.(range);

    $open = false;
  });

  // svelte-ignore non_reactive_update
  let monthWrapper: HTMLDivElement | null;
  // svelte-ignore non_reactive_update
  let monthContent: HTMLDivElement | null;
  // set explicit wrapper height
  // so we can transition when displayed month changes
  const unsubscribeOpen = open.subscribe(async (o) => {
    if (!o) return;
    setCalendarHeight();
  });
  const unsubscribeMonths = months.subscribe(setCalendarHeight);

  async function setCalendarHeight() {
    await tick();

    if (!monthWrapper || !monthContent) return;
    monthWrapper.style.height = monthContent.offsetHeight + "px";
  }

  onDestroy(() => {
    unsubscribeOpen();
    unsubscribeMonths();
  });
</script>

<div class="flex flex-col items-center text-sm select-none">
  <button
    {...$trigger} use:$trigger.action
    class="flex h-7 items-center rounded-lg bg-mono-000 shadow-border focus-ring hover:bg-mono-200"
    type="button"
  >
    <div class="flex items-center gap-2 p-2">
      <span class="sr-only">{label}</span>
      <Calendar aria-hidden class="text-mono-600" />
      <span class="w-45 truncate text-left">
        {formatDateRange(displayedRange)}
      </span>
    </div>

    <div aria-hidden="true" class="flex size-7 items-center justify-center">
      <ChevronDown aria-hidden />
    </div>
  </button>

  {#if $open}
    <div
      in:fly={{ duration: 120, y: -10 }}
      out:fly={{ duration: 120 }}
      {...$content} use:$content.action
      class="z-10 flex flex-col items-stretch rounded-2xl bg-popover-bg text-sm text-popover-primary shadow-popover backdrop-blur-popover"
    >
      <div {...$calendar} use:$calendar.action class="p-2 pb-0 select-none">
        <header class="flex items-center justify-between p-1">
          <button {...$prevButton} use:$prevButton.action class="arrow-button" type="button">
            <ChevronLeft />
          </button>
          <div {...$heading} use:$heading.action class="text-popover-primary">
            {$headingValue}
          </div>
          <button {...$nextButton} use:$nextButton.action class="arrow-button" type="button">
            <ChevronRight />
          </button>
        </header>

        <div class="month-wrapper -m-1 overflow-hidden p-1" bind:this={monthWrapper}>
          {#each $months as month}
            <div bind:this={monthContent} class="pb-4">
              <table {...$grid} use:$grid.action class="h-fit w-full">
                <thead aria-hidden="true">
                  <tr>
                    {#each weekdayHeaders as day}
                      <th>
                        <div
                          class="flex size-8 items-center justify-center font-normal text-popover-tertiary"
                        >
                          {day}
                        </div>
                      </th>
                    {/each}
                  </tr>
                </thead>

                <tbody>
                  {#each month.weeks as weekDates}
                    <tr>
                      {#each weekDates as date}
                        {@const cellCal = toCalendarDate(date)}
                        {@const startCal = $selectedRange?.start
                          ? toCalendarDate($selectedRange.start)
                          : null}
                        {@const endCal = $selectedRange?.end
                          ? toCalendarDate($selectedRange.end)
                          : null}
                        {@const insideRange =
                          startCal != null &&
                          endCal != null &&
                          cellCal.compare(startCal) > 0 &&
                          cellCal.compare(endCal) < 0}
                        {@const isRangeStart = startCal != null && cellCal.compare(startCal) === 0}
                        {@const isRangeEnd = endCal != null && cellCal.compare(endCal) === 0}
                        {@const isMaxDate =
                          maxValue && toCalendarDate(maxValue).compare(cellCal) === 0}
                        {@const __MELTUI_BUILDER_0__ = $cell(date, month.value)}

                        <td
                          role="gridcell"
                          aria-disabled={$isDateDisabled(date) || $isDateUnavailable(date)}
                        >
                          <div
                            {...__MELTUI_BUILDER_0__} use:__MELTUI_BUILDER_0__.action
                            class={[
                              "flex size-8 cursor-pointer items-center justify-center rounded-md text-popover-primary focus-ring-popover",
                              "data-[in-range=true]:bg-mono-500/50 data-[range-end=true]:rounded-l-none data-[range-start=true]:rounded-r-none data-[in-range=true]:dark:bg-mono-300",
                              "data-selected:bg-mono-500/50  data-selected:dark:bg-mono-300",
                            ]}
                            data-in-range={insideRange}
                            data-range-start={isRangeStart}
                            data-range-end={isRangeEnd}
                            {...isMaxDate
                              ? {
                                  "onm-click": (e: Event) => maybeSelectMaxDate(e, date),
                                  onkeydowncapture: (e: KeyboardEvent) =>
                                    handleMaxDateKeydown(e, date),
                                }
                              : ({} as any)}
                          >
                            {date.day}
                          </div>
                        </td>
                      {/each}
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/each}
        </div>
      </div>

      {#if showPresets}
        <div aria-hidden="true" class="mb-2 h-px bg-popover-divider"></div>
        <ul aria-label="date range presets" class="flex flex-col items-stretch px-2 pb-2">
          {#each DATE_RANGE_PRESETS as preset}
            {@const presetStart = getPresetRange(preset.key, maxValue).start}
            {#if !minValue || toCalendarDate(presetStart).compare(toCalendarDate(minValue)) >= 0}
              <li>
                <button
                  type="button"
                  class="flex h-8 w-full items-center rounded-md px-2 focus-ring-popover hover:bg-popover-item-hover-bg"
                  onclick={() => applyPreset(preset)}
                  >{preset.label}
                </button>
              </li>
            {/if}
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</div>

<style>
  @reference "#tailwind.css";

  .month-wrapper {
    @media (prefers-reduced-motion: no-preference) {
      interpolate-size: numeric-only;
      @apply transition-[height] duration-150;
    }
  }

  .arrow-button {
    @apply -m-1 rounded-lg p-1 focus-ring-popover transition-opacity duration-150 not-disabled:hover:opacity-50;
  }

  [data-melt-calendar-prevbutton][data-disabled],
  [data-melt-calendar-nextbutton][data-disabled] {
    @apply pointer-events-none opacity-40;
  }

  [data-melt-calendar-cell] {
    @apply hover:bg-popover-item-hover-bg;

    &[data-selected] {
      &[data-selection-start],
      &[data-selection-end] {
        @apply bg-popover-item-active-bg text-popover-item-active;
      }
    }

    &[data-in-range="true"] {
      @apply rounded-none;
    }

    /* range starts + ends on same day */
    &[data-range-end="true"][data-range-start="true"] {
      @apply rounded-md;
    }

    &[data-disabled] {
      @apply pointer-events-none;
    }

    &[data-unavailable] {
      @apply pointer-events-none line-through;
    }

    &[data-outside-visible-months],
    &[data-disabled] {
      @apply text-popover-tertiary;
    }

    &[data-outside-month] {
      @apply opacity-0;
    }

    &[data-outside-visible-months],
    &[data-outside-month] {
      @apply pointer-events-none cursor-default hover:bg-transparent;
    }
  }
</style>
